import { useCallback, useEffect, useRef, useState } from 'react'
import Notiflix from 'notiflix'
import { FaCloudUploadAlt, FaPaperclip, FaPlus, FaTimes } from 'react-icons/fa'
import api, { apiUpload } from '../../../../../../helpers/apiRequester'
import {
    Avatar,
    Button,
    Card,
    EmptyState,
    Field,
    FieldGrid,
    IconButton,
    Input,
    Modal,
    NativeSelect,
    ResultLine,
    Spinner,
    StatusPill,
    Toolbar,
    cx,
} from '../../../../../../ui'
import { RichTextEditor } from './richTextEditor'
import styles from './styles.module.scss'

interface NoteAttachment {
    id: number
    filename: string
    mimetype?: string
    size_bytes?: number
    url: string | null
}

interface ClientNote {
    id: number
    note_taker: string
    contact_platform: string
    contact_at: string
    note_html: string
    meta: Record<string, string> | null
    created_at: string
    attachments: NoteAttachment[]
}

interface MetaFieldRow {
    label: string
    value: string
}

interface NotesTabProps {
    clientId: number
    origin: string
    clientEmail?: string
}

// Quick-add suggestions for commonly recorded note details.
const SUGGESTED_META_FIELDS = ['Call Outcome', 'Reference Number', 'Follow-up Date', 'Call Duration']

const CONTACT_PLATFORMS = [
    { value: 'phone', label: 'Phone' },
    { value: 'email', label: 'Email' },
    { value: 'sms', label: 'SMS' },
    { value: 'letter', label: 'Letter' },
    { value: 'in_person', label: 'In Person' },
    { value: 'other', label: 'Other' },
]

const platformLabel = (value: string) => CONTACT_PLATFORMS.find((p) => p.value === value)?.label || value

const formatDateTime = (value: string) => {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

// Local "now" in the format datetime-local inputs expect (YYYY-MM-DDTHH:MM).
const nowForInput = () => {
    const now = new Date()
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset())
    return now.toISOString().slice(0, 16)
}

const formatFileSize = (bytes?: number) => {
    if (!bytes) return ''
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export const NotesTab = ({ clientId, origin, clientEmail }: NotesTabProps) => {
    const [notes, setNotes] = useState<ClientNote[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [showAddForm, setShowAddForm] = useState(false)

    // Add-note form state
    const [contactPlatform, setContactPlatform] = useState('phone')
    const [contactAt, setContactAt] = useState(nowForInput())
    const [noteHtml, setNoteHtml] = useState('')
    const [noteError, setNoteError] = useState<string | null>(null)
    const [metaFields, setMetaFields] = useState<MetaFieldRow[]>([])
    const [attachments, setAttachments] = useState<File[]>([])
    const [isDraggingFiles, setIsDraggingFiles] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)
    // Bumped after a successful save to remount (and therefore clear) the editor.
    const [editorKey, setEditorKey] = useState(0)

    const loadNotes = useCallback(async () => {
        setIsLoading(true)
        try {
            const response = await api({
                url: 'client-notes-list',
                data: { client_id: clientId, origin },
            })
            setNotes(response.data.success ? response.data.notes : [])
        } catch (error) {
            console.error('Error loading client notes:', error)
            setNotes([])
        } finally {
            setIsLoading(false)
        }
    }, [clientId, origin])

    useEffect(() => {
        loadNotes()
    }, [loadNotes])

    const resetForm = () => {
        setContactPlatform('phone')
        setContactAt(nowForInput())
        setNoteHtml('')
        setNoteError(null)
        setMetaFields([])
        setAttachments([])
        setEditorKey((key) => key + 1)
    }

    const openComposer = () => {
        resetForm()
        setShowAddForm(true)
    }

    const closeComposer = () => {
        if (isSaving) return
        setShowAddForm(false)
    }

    const updateMetaField = (index: number, patch: Partial<MetaFieldRow>) => {
        setMetaFields((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)))
    }

    const addFiles = (files: File[]) => {
        if (files.length) setAttachments((current) => [...current, ...files])
    }

    const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
        addFiles(Array.from(e.target.files || []))
        e.target.value = ''
    }

    const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setIsDraggingFiles(false)
        addFiles(Array.from(e.dataTransfer.files || []))
    }

    const handleSubmit = async () => {
        // Treat markup-only editor output (e.g. "<br>") as empty.
        const textOnly = noteHtml.replace(/<[^>]*>/g, '').trim()
        if (!textOnly) {
            setNoteError('Write the note before saving.')
            return
        }
        setNoteError(null)

        setIsSaving(true)
        try {
            const formData = new FormData()
            formData.append('client_id', String(clientId))
            formData.append('origin', origin)
            if (clientEmail) formData.append('client_email', clientEmail)
            formData.append('contact_platform', contactPlatform)
            formData.append('contact_at', new Date(contactAt).toISOString())
            formData.append('note_html', noteHtml)

            const meta = Object.fromEntries(
                metaFields
                    .map((row) => [row.label.trim(), row.value.trim()])
                    .filter(([label, value]) => label && value),
            )
            if (Object.keys(meta).length) formData.append('meta_fields', JSON.stringify(meta))

            attachments.forEach((file) => formData.append('attachments', file))

            const response = await apiUpload('client-notes-create', formData)

            if (!response.data.success) {
                Notiflix.Report.failure(
                    'Save Failed',
                    response.data.message || 'The note could not be saved. Please try again.',
                    'OK',
                )
                return
            }

            if (response.data.failed_attachments?.length) {
                Notiflix.Report.warning(
                    'Note Saved',
                    `The note was saved, but these attachments failed to upload: ${response.data.failed_attachments.join(', ')}`,
                    'OK',
                )
            }

            resetForm()
            setShowAddForm(false)
            await loadNotes()
        } catch (error) {
            console.error('Error saving client note:', error)
            Notiflix.Report.failure('Save Failed', 'An error occurred whilst saving the note. Please try again.', 'OK')
        } finally {
            setIsSaving(false)
        }
    }

    const availableSuggestions = SUGGESTED_META_FIELDS.filter(
        (suggestion) => !metaFields.some((row) => row.label.trim().toLowerCase() === suggestion.toLowerCase()),
    )

    return (
        <div className={styles.notesTab}>
            <Toolbar
                end={
                    <Button size="sm" icon={<FaPlus />} onClick={openComposer}>
                        Add note
                    </Button>
                }
            >
                <ResultLine>
                    {isLoading
                        ? 'Loading notes…'
                        : `${notes.length} ${notes.length === 1 ? 'note' : 'notes'} on record`}
                </ResultLine>
            </Toolbar>

            {isLoading ? (
                <Spinner block text="Loading notes…" />
            ) : notes.length === 0 ? (
                <EmptyState
                    title="No notes yet"
                    description="Log calls, emails and letters here so the whole team can see the client's contact history."
                    action={
                        <Button size="sm" icon={<FaPlus />} onClick={openComposer}>
                            Add the first note
                        </Button>
                    }
                />
            ) : (
                <ol className={styles.timeline}>
                    {notes.map((note) => (
                        <li key={note.id} className={styles.timelineItem}>
                            <span className={styles.timelineMarker} aria-hidden="true" />
                            <Card padding="sm">
                                <div className={styles.noteHeader}>
                                    <span className={styles.noteDate}>{formatDateTime(note.contact_at)}</span>
                                    <StatusPill tone="accent" size="sm">
                                        {platformLabel(note.contact_platform)}
                                    </StatusPill>
                                    <span className={styles.noteTaker}>
                                        <Avatar name={note.note_taker || '?'} size="sm" />
                                        {note.note_taker}
                                    </span>
                                </div>

                                {note.meta && Object.keys(note.meta).length > 0 && (
                                    <dl className={styles.noteMeta}>
                                        {Object.entries(note.meta).map(([label, value]) => (
                                            <div key={label}>
                                                <dt>{label}</dt>
                                                <dd>{value}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                )}

                                {/* note_html is sanitised server-side before storage */}
                                <div className={styles.noteBody} dangerouslySetInnerHTML={{ __html: note.note_html }} />

                                {note.attachments.length > 0 && (
                                    <div className={styles.noteAttachments}>
                                        {note.attachments.map((attachment) => (
                                            <Button
                                                key={attachment.id}
                                                as="a"
                                                size="sm"
                                                href={attachment.url || '#'}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                icon={<FaPaperclip />}
                                            >
                                                {attachment.filename}
                                                {attachment.size_bytes
                                                    ? ` (${formatFileSize(attachment.size_bytes)})`
                                                    : ''}
                                            </Button>
                                        ))}
                                    </div>
                                )}
                            </Card>
                        </li>
                    ))}
                </ol>
            )}

            <Modal
                open={showAddForm}
                onClose={closeComposer}
                size="xl"
                title="Add note"
                subtitle={clientEmail ? `Contact note for ${clientEmail}` : 'Contact note'}
                busy={isSaving}
                closeOnBackdrop={!isSaving}
                footer={
                    <>
                        <Button onClick={closeComposer} disabled={isSaving}>
                            Cancel
                        </Button>
                        <Button variant="primary" loading={isSaving} onClick={() => void handleSubmit()}>
                            Save note
                        </Button>
                    </>
                }
            >
                <div className={styles.composer}>
                    <FieldGrid minColumn={220}>
                        <Field label="Contact platform" required>
                            {(id) => (
                                <NativeSelect
                                    id={id}
                                    value={contactPlatform}
                                    onChange={(e) => setContactPlatform(e.target.value)}
                                    options={CONTACT_PLATFORMS}
                                />
                            )}
                        </Field>
                        <Field label="Date & time of contact" required>
                            {(id) => (
                                <Input
                                    id={id}
                                    type="datetime-local"
                                    value={contactAt}
                                    onChange={(e) => setContactAt(e.target.value)}
                                    required
                                />
                            )}
                        </Field>
                    </FieldGrid>

                    <Field label="Note" required error={noteError}>
                        <RichTextEditor
                            key={editorKey}
                            onChange={(html) => {
                                setNoteHtml(html)
                                if (noteError) setNoteError(null)
                            }}
                        />
                    </Field>

                    <Field
                        label="Additional fields"
                        optional
                        hint="Capture structured details alongside the note — pick a suggestion or add your own."
                    >
                        <Card muted padding="sm">
                            <div className={styles.metaPanel}>
                                <div className={styles.metaChips}>
                                    {availableSuggestions.map((suggestion) => (
                                        <Button
                                            key={suggestion}
                                            size="sm"
                                            icon={<FaPlus />}
                                            onClick={() =>
                                                setMetaFields((rows) => [...rows, { label: suggestion, value: '' }])
                                            }
                                        >
                                            {suggestion}
                                        </Button>
                                    ))}
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        icon={<FaPlus />}
                                        onClick={() => setMetaFields((rows) => [...rows, { label: '', value: '' }])}
                                    >
                                        Custom field
                                    </Button>
                                </div>

                                {metaFields.length > 0 && (
                                    <div className={styles.metaRows}>
                                        {metaFields.map((row, index) => (
                                            <div key={index} className={styles.metaRow}>
                                                <Input
                                                    small
                                                    aria-label="Field name"
                                                    placeholder="Field, e.g. Call outcome"
                                                    value={row.label}
                                                    onChange={(e) => updateMetaField(index, { label: e.target.value })}
                                                />
                                                <Input
                                                    small
                                                    aria-label="Field value"
                                                    placeholder="Value"
                                                    value={row.value}
                                                    onChange={(e) => updateMetaField(index, { value: e.target.value })}
                                                />
                                                <IconButton
                                                    label="Remove field"
                                                    icon={<FaTimes />}
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() =>
                                                        setMetaFields((rows) => rows.filter((_, i) => i !== index))
                                                    }
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </Card>
                    </Field>

                    <Field label="Attachments" optional>
                        {(id) => (
                            <>
                                <input
                                    ref={fileInputRef}
                                    id={id}
                                    type="file"
                                    multiple
                                    className={styles.hiddenFileInput}
                                    onChange={handleFileSelection}
                                />
                                <div
                                    className={cx(styles.dropzone, isDraggingFiles && styles.dropzoneActive)}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => fileInputRef.current?.click()}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault()
                                            fileInputRef.current?.click()
                                        }
                                    }}
                                    onDragOver={(e) => {
                                        e.preventDefault()
                                        setIsDraggingFiles(true)
                                    }}
                                    onDragLeave={() => setIsDraggingFiles(false)}
                                    onDrop={handleFileDrop}
                                >
                                    <FaCloudUploadAlt aria-hidden="true" />
                                    <span className={styles.dropzoneTitle}>Drop files here, or click to browse</span>
                                    <span className={styles.dropzoneHint}>
                                        Call recordings, letters, screenshots or any supporting documents
                                    </span>
                                </div>
                                {attachments.length > 0 && (
                                    <ul className={styles.pendingAttachments}>
                                        {attachments.map((file, index) => (
                                            <li key={`${file.name}-${index}`}>
                                                <FaPaperclip aria-hidden="true" />
                                                <span className={styles.pendingName}>{file.name}</span>
                                                <span className={styles.pendingSize}>{formatFileSize(file.size)}</span>
                                                <IconButton
                                                    label={`Remove ${file.name}`}
                                                    icon={<FaTimes />}
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() =>
                                                        setAttachments((files) => files.filter((_, i) => i !== index))
                                                    }
                                                />
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </>
                        )}
                    </Field>
                </div>
            </Modal>
        </div>
    )
}
